import { sample, getTileId } from './letters';
import type {
  Board,
  Cell,
  Coord,
  HighlightColors,
  Match,
  Tile,
  TwistTile,
} from '../types';
import { dictionary, dictionaryLoaded } from './dictionary.svelte';
import { delay } from '~/animations/utils.svelte';

const randInt = (max: number) => Math.floor(Math.random() * max);

export const copyBoard = <T extends object>(board: T[][]) =>
  (board = board.map((col) => col.map((t) => ({ ...t }))));

export const resetBoard = async (dims: Coord) => {
  let board: Board = [];

  for (let i = 0; i < dims[0]; i++) {
    board.push([]);
    for (let j = 0; j < dims[1]; j++) {
      const [, tile] = sample([], 1);
      board[i].push(tile);
    }
  }

  // TODO verify this
  // first load, dictionary may not be loaded yet.
  // console.log(dictionaryLoaded, dictionary)
  while (!dictionaryLoaded.current) {
    await delay(100);
  }
  await dictionaryLoaded.current;
  // console.log(dictionaryLoaded, dictionary)
  let { words } = await dictionary.findWords(board, 'classic');
  while (words.length) {
    for (const word of words) {
      const a = word.coords[randInt(word.word.length)];
      const b = word.coords[randInt(word.word.length)];
      board = swap(board, a, b);
    }
    words = (await dictionary.findWords(board, 'classic')).words;
  }

  for (const [i, col] of board.entries()) {
    for (const [j, row] of col.entries()) {
      // reset multipliers
      board[i][j].multiplier = 1;
    }
  }

  // start game with 3 2x multipliers
  for (let i = 0; i < 3; i++) {
    const col = randInt(dims[0]);
    const row = randInt(dims[1]);
    board[col][row].multiplier = 2;
  }

  return board;
};

// TODO move this into method
export const swap = <T extends object>(
  board: T[][],
  [i, j]: Coord,
  [i2, j2]: Coord,
) => {
  // TODO use $state.snapshot?
  board = copyBoard(board);
  const first = board[i][j];
  const second = board[i2][j2];
  if (first && second) {
    board[i][j] = second;
    board[i2][j2] = first;
  }
  return board;
};

const d: Coord[] = [
  [0, 0],
  [0, 1],
  [1, 1],
  [1, 0],
];

export const swapCells = <T extends Tile>(
  board: Board<T>,
  [i, j]: Coord,
  [i2, j2]: Coord,
) => {
  const [ti, tj] = [j * 2, i * 2];
  const [ti2, tj2] = [j2 * 2, i2 * 2];
  for (const [di, dj] of d) {
    board = swap(board, [ti + di, tj + dj], [ti2 + di, tj2 + dj]);
  }
  return board;
};

export const rotateCell = <T extends Tile>(board: Board<T>, [i, j]: Coord) => {
  const [ox, oy] = [j * 2, i * 2];
  const dOffset = d.map(([dx, dy]) => [dx + ox, dy + oy] as Coord);
  dOffset.push(dOffset.shift()!);
  let temp = board[ox][oy];
  let temp2 = undefined;
  for (const [i, [dx, dy]] of Object.entries(dOffset)) {
    temp2 = board[dx][dy];
    board[dx][dy] = temp;
    temp = temp2;
  }
  return board;
};

export const mapTilesToCells = (board: Board<TwistTile>) => {
  const cells: Cell[][] = [];
  for (const [i, row] of Object.entries(board)) {
    for (const [j, tile] of Object.entries(row)) {
      const ci = Math.floor(+j / 2);
      const cj = Math.floor(+i / 2);

      if (!cells[ci]) {
        cells[ci] = [];
      }
      if (!cells[ci][cj]) {
        cells[ci][cj] = {
          id: getTileId(),
          angle: 0,
          tiles: [],
        };
      }
      cells[ci][cj].tiles.push(tile);
    }
  }
  return cells;
};

// TODO this could be in the WebWorker
const points: Record<number, number> = {
  4: 1,
  5: 2,
  6: 4,
  7: 8,
  8: 8,
  9: 8,
  10: 8,
  11: 8,
  12: 8,
  13: 8,
  14: 8,
};

export const getSwapBonus = (word: Match, chain: number) => {
  // give full swap bonus for second word in intersection
  if (chain > 0) return word.stars ?? 0;
  const wordLength = word.word
    .map((t) => t.letter.length)
    .reduce((a, b) => a + b);
  return (points[wordLength] ?? 0) + (word.stars ?? 0);
};

export const highlightColors: Record<number, HighlightColors> = {
  6: 'red',
  5: 'orange',
  4: 'green',
  3: 'blue',
  2: 'purple',
  1: 'red',
};

// TODO
export const dailyTheme: Record<number, string> = {
  6: '#581845',
  5: '#900C3F',
  4: '#C70039',
  3: '#FF5733',
  2: '#FFC30F',
  1: '#581845',
};